import { Box, Button, Flex, Group, Space, Stack } from '@mantine/core';
import { IconChevronLeft, IconRefresh } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { aiAgentApis } from '../../../../../../shared/apis/ai-agent/apis';
import { handleError } from '../../../../../ui/err/handleError';
import * as C from '../../../../../ui/common/CommonStyles';
import ContextInfo from './component/ContextInfo/ContextInfo';
import ConversionResult from './component/ConversionResult/ConversionResult';
import ConversionStepper from './component/ConversionStepper/ConversionStepper';
import ProgressSummary from './component/Progress/ProgressSummary';
import { TRACKING_CONFIGS } from './constants/conversionTracking';
import { getStepStatusOverride } from './utils/getStepStructure';

import type { AxiosError } from 'axios';
import type { IGetFileUploadStatusParams } from '../../../../../../shared/apis/ai-agent/file/types';
import type { AxiosCommonError } from '../../../../../../shared/types';
import type { TrackingType } from './types';

export default function TrackingStatus(props: { trackingType: TrackingType }) {
  const { trackingType } = props;
  const navigate = useNavigate();
  const { companyId, fileUploadId } = useParams();
  const [searchParams] = useSearchParams();
  const companyName = searchParams.get('companyName') || '';
  const fileName = searchParams.get('fileName') || '';
  const trackingConfig = TRACKING_CONFIGS[trackingType];

  const sendData: IGetFileUploadStatusParams = {
    companyId: Number(companyId),
    fileUploadId: Number(fileUploadId),
  };

  const { data: uploadHistory, refetch: refetchUploadHistory } = useQuery(
    ['getUploadHistory', companyId, fileUploadId],
    () => aiAgentApis.file.getFileUploadStatus(sendData),
    {
      select: (data) => data.data,
      refetchInterval: 1000 * 5, // 5 seconds
    },
  );

  const currentProcess = uploadHistory?.status ?? null;
  const currentStatus = getStepStatusOverride(trackingType, currentProcess, uploadHistory?.failReason);

  const handleRefresh = () => {
    void refetchUploadHistory();
  };

  const { mutate: getPresignedUrl } = useMutation((id: number) => aiAgentApis.model.postFileDownload(id), {
    onSuccess: (data) => {
      const { presignedUrl, fileKey } = data.data;
      const fileName = fileKey.split('/').pop();
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = `${fileName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: (error: AxiosError<AxiosCommonError>) => {
      handleError(error);
    },
  });

  return (
    <div>
      <C.TitleBox>
        <div className="title">문서 변환 진행상황</div>
      </C.TitleBox>
      <C.ListBox>
        <Flex justify="flex-end" align="center" mb="md" gap="md">
          {/* <Button
            variant="default"
            leftSection={<IconDownload size={16} />}
            onClick={() => {
              getPresignedUrl(Number(fileUploadId));
            }}
          >
            원본 다운로드
          </Button> */}
          <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={handleRefresh}>
            새로고침
          </Button>
        </Flex>
        <Box mb="md">
          <ContextInfo fileName={fileName} companyName={companyName} />
        </Box>
        <Stack
          gap="lg"
          py="xl"
          style={(theme) => ({
            border: `1px solid ${theme.colors.gray[3]}`,
            borderRadius: theme.radius.sm,
          })}
        >
          <ProgressSummary
            trackingType={trackingType}
            currentProcess={currentProcess}
            progressCurrent={uploadHistory?.progressCurrent ?? 0}
            progressTotal={uploadHistory?.progressTotal ?? 0}
          />
          <ConversionStepper config={trackingConfig} currentProcess={currentProcess} currentStatus={currentStatus} />
        </Stack>

        <Space h="xl" />
        <ConversionResult trackingType={trackingType} uploadHistory={uploadHistory} />
      </C.ListBox>
      <Group justify="center" my="lg">
        <Button variant="default" onClick={() => navigate(-1)} leftSection={<IconChevronLeft size={16} />}>
          뒤로가기
        </Button>
      </Group>
    </div>
  );
}
